// client/src/components/ClientSettings.js

import React, { useState, useEffect, useRef, useCallback, useMemo, useContext } from 'react';
import ReactDOM from 'react-dom';
import { AuthContext } from '../../AuthContext';
import { firestore, storage } from '../../firebase';
import {
  getDoc,
  doc,
  setDoc,
  updateDoc,
  collection,
  getDocs,
  onSnapshot,
  serverTimestamp,
  query,
  limit as fsLimit,
} from 'firebase/firestore';
import {
  getAuth,
  onAuthStateChanged,
  EmailAuthProvider,
  linkWithCredential,
  reauthenticateWithCredential,
  updateEmail as firebaseUpdateEmail
} from 'firebase/auth';
import { ref, getDownloadURL, listAll, getMetadata, uploadBytes } from 'firebase/storage';
import '../CSS/Dashboard.css';
import { useNavigate } from 'react-router-dom';
import ClientNavbar from './ClientNavbar';
import { LanguageSelector } from '../../i18n';
import { useTranslation } from 'react-i18next';
import { FaUser } from 'react-icons/fa';
import { FiEdit2, FiChevronRight } from 'react-icons/fi';
import { sanitize } from '../../utils/sanitize';
import AccountSwitcher from '../DIVERS/AccountSwitcher';
import { logoutSafely } from '../../utils/logoutSafely';

// >>> règle MDP front unifiée
import { REGEX as PASSWORD_REGEX, POLICY as PASSWORD_POLICY } from '../../policies/passwordPolicy';
import PasswordField from '../shared/PasswordField';

/* ================================
   Caching & coûts (tuning)
================================ */
const PROFILE_PHOTO_TTL_MS   = 7 * 24 * 60 * 60 * 1000; // 7 jours
const INV_SCAN_TTL_MS        = 15 * 60 * 1000;          // 15 minutes (scan borné)
// Firestore rules: list queries require limit <= 50
const INV_LIMIT_SCAN         = 50;                      // max entreprises scannées côté client
const SETTINGS_CACHE_KEY     = 'clientSettings';
const EMAIL_PROPAGATION_LIMIT = 120;                    // borne sécurité: docs entreprises à inspecter max

const INV_CACHE_KEY = (email) => `inv_cache_${email}`;                  // { items:[{id,...}], companies:[id,id], ts }
const INV_KNOWN_COMPANIES_KEY = (email) => `inv_known_companies_${email}`; // { companies:[id], ts }
const PROFILE_PHOTO_KEY = (uid) => `profilePhotoURL_${uid}`;            // string ou JSON { url, ts }
const SETTINGS_INDEX_CACHE_KEY = (uid) => `settings_index_${uid}`;
const nowMs = () => Date.now();
const SETTINGS_INDEX_TTL_MS = 6 * 60 * 60 * 1000; // 6h pour le cache local des settings

/* ================================
   Helpers cache & sécurité
================================ */
const fromCache = (key, ttl) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;

    // Accept JSON {ts,...} ou string legacy
    if (raw.startsWith('{') || raw.startsWith('[')) {
      const obj = JSON.parse(raw);
      if (ttl == null) return obj;
      if (!obj || typeof obj !== 'object' || typeof obj.ts !== 'number') return null;
      if (Date.now() - obj.ts > ttl) return null;
      return obj;
    }
    if (ttl == null) return raw;
    return raw;
  } catch {
    return null;
  }
};
const putCache = (key, payload) => {
  try {
    const value = typeof payload === 'string' ? payload : JSON.stringify({ ...payload, ts: Date.now() });
    localStorage.setItem(key, value);
  } catch {}
};
const authInstance = getAuth();

// -------------------------------
// Index helpers (minimise reads)
// -------------------------------
const serializeIndexPayload = (payload = {}) => ({
  profilePhotoUrl: payload.profilePhotoUrl || null,
  companies: Array.isArray(payload.companies) ? payload.companies.slice(0, INV_LIMIT_SCAN) : [],
  pendingInvitations: Array.isArray(payload.pendingInvitations)
    ? payload.pendingInvitations.slice(0, INV_LIMIT_SCAN)
    : [],
  acceptedCompanyId: payload.acceptedCompanyId || null,
});

const indexSignature = (payload) => JSON.stringify(serializeIndexPayload(payload));

const saveIndexCache = (uid, payload) => {
  if (!uid || !payload) return;
  const normalized = serializeIndexPayload(payload);
  const updatedAt = payload.updatedAt || nowMs();
  try {
    localStorage.setItem(
      SETTINGS_INDEX_CACHE_KEY(uid),
      JSON.stringify({ ts: updatedAt, payload: { ...normalized, updatedAt } })
    );
  } catch {}
};

const loadIndexCache = (uid) => {
  if (!uid) return null;
  try {
    const raw = localStorage.getItem(SETTINGS_INDEX_CACHE_KEY(uid));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const ts = parsed?.ts || 0;
    if (!ts || nowMs() - ts > SETTINGS_INDEX_TTL_MS) return null;
    return parsed.payload || null;
  } catch {
    return null;
  }
};

const persistSettingsIndex = async (
  uid,
  partialPayload = {},
  merge = false,
  signatureRef,
  tsRef
) => {
  if (!uid) return;
  const cached = merge ? loadIndexCache(uid) || {} : {};
  const nextPayload = { ...cached, ...partialPayload };
  const normalized = serializeIndexPayload(nextPayload);
  const updatedAt = nowMs();
  const sig = indexSignature({ ...normalized, updatedAt });
  if (signatureRef && signatureRef.current === sig) return;
  if (signatureRef) signatureRef.current = sig;
  saveIndexCache(uid, { ...normalized, updatedAt });
  if (tsRef) tsRef.current = updatedAt;
};

/* ================================
   Composant
================================ */
const ClientSettings = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { reloadEmailVerified } = useContext(AuthContext);

  const [user, setUser] = useState(null);

  // Email
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [editedEmail, setEditedEmail] = useState('');

  // Création compte (anonyme -> email/password)
  const [newEmail, setNewEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [isPwdValid, setIsPwdValid] = useState(true); // validation live

  // Pseudo
  const [newPseudo, setNewPseudo] = useState('');
  const [isEditingPseudo, setIsEditingPseudo] = useState(false);

  // Invitations & entreprise (hybride live-like)
  const [invitations, setInvitations] = useState([]);
  const [currentCompany, setCurrentCompany] = useState(null);

  // Etats modales
  const [showModal, setShowModal] = useState(false);
  const [showCreateAccountModal, setShowCreateAccountModal] = useState(false);

  // Photo profil
  const [profilePhotoURL, setProfilePhotoURL] = useState(null);
  const fileInputRef = useRef(null);

  // Listeners invits par entreprise
  const invUnsubsRef = useRef({}); // { [companyId]: () => void }
  const mountedRef = useRef(true);
  const indexSigRef = useRef('');
  const lastIndexTsRef = useRef(0);

  /* ================================
     Photo de profil: cache + refresh silencieux
  ================================= */
  const fetchProfilePhoto = useCallback(async () => {
    const userId = authInstance.currentUser?.uid;
    if (!userId) return;

    // Sert d'abord le cache (si TTL OK)
    const cached = fromCache(PROFILE_PHOTO_KEY(userId), PROFILE_PHOTO_TTL_MS);
    if (cached) {
      const url = typeof cached === 'string' ? cached : cached.url;
      if (url) setProfilePhotoURL(url);
    }

    // Index fraîche ? (prioritaire pour éviter listAll)
    const idx = loadIndexCache(userId);
    if (idx?.profilePhotoUrl) {
      setProfilePhotoURL((prev) => prev || idx.profilePhotoUrl);
      // si index récent, on peut s'arrêter
      if (nowMs() - (idx.updatedAt || 0) < PROFILE_PHOTO_TTL_MS) return;
    }

    // Refresh Storage silencieux uniquement si pas de cache
    if (!cached && navigator.onLine) {
      try {
        const profilePhotoRef = ref(storage, `${userId}/photo-profil/`);
        const result = await listAll(profilePhotoRef);
        if (!mountedRef.current) return;

        if (!result.items.length) {
          setProfilePhotoURL(null);
          putCache(PROFILE_PHOTO_KEY(userId), { url: null });
          return;
        }
        // fetch minimal metadata pour déterminer le plus récent
        const itemsWithMetadata = await Promise.all(
          result.items.map(async (item) => {
            const metadata = await getMetadata(item);
            const timeCreated = metadata.timeCreated ? new Date(metadata.timeCreated).getTime() : 0;
            return { item, ts: timeCreated };
          })
        );
        if (!mountedRef.current) return;

        itemsWithMetadata.sort((a, b) => b.ts - a.ts);
        const latestItem = itemsWithMetadata[0].item;
        const url = await getDownloadURL(latestItem);
        if (!mountedRef.current) return;

        setProfilePhotoURL(url);
        putCache(PROFILE_PHOTO_KEY(userId), { url });
        // index best-effort
        persistSettingsIndex(userId, { profilePhotoUrl: url }, /*merge*/true, indexSigRef, lastIndexTsRef);
      } catch (error) {
        // silencieux
        // eslint-disable-next-line no-console
        console.error('fetchProfilePhoto error:', error);
      }
    }
  }, []);

const handleProfilePhotoChange = async (e) => {
  const userId = authInstance.currentUser?.uid;
  if (!userId) return;

  const file = e.target.files?.[0];
  if (file) {
    const fileRef = ref(storage, `${userId}/photo-profil/${Date.now()}_${file.name}`);
    try {
      await uploadBytes(fileRef, file);
      // mise à jour immédiate UX via URL fraîche
      const url = await getDownloadURL(fileRef);
      if (!mountedRef.current) return;
      setProfilePhotoURL(url);
      putCache(PROFILE_PHOTO_KEY(userId), { url }); // reset TTL
      persistSettingsIndex(userId, { profilePhotoUrl: url }, /*merge*/true, indexSigRef, lastIndexTsRef);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Erreur lors du téléchargement de la photo de profil:", error);
    }
  }
};

  /* ================================
     Invitations: scan borné + cache + listeners ciblés
  ================================= */
  const stopAllInvListeners = useCallback(() => {
    Object.values(invUnsubsRef.current).forEach((fn) => fn && fn());
    invUnsubsRef.current = {};
  }, []);

  // Attache un listener temps réel sur un doc entreprise précis
  const attachInvListenerForCompany = useCallback((companyId, email) => {
    if (!companyId || !email || invUnsubsRef.current[companyId]) return;

    const compRef = doc(firestore, 'entreprises', companyId);
    invUnsubsRef.current[companyId] = onSnapshot(
      compRef,
      (snap) => {
        const data = snap.data() || {};
        const arr = Array.isArray(data.invitations) ? data.invitations : [];

        // pending pour UI
        const pendings = arr
          .filter(inv => inv.employeeEmail === email && inv.status === 'pending')
          .map(inv => ({ id: companyId, ...inv }));

        // merge propre: on remplace les items de cette entreprise
        setInvitations((prev) => {
          const others = prev.filter(x => x.id !== companyId);
          return [...others, ...pendings];
        });

        // accepted pour currentCompany (évite re-set identiques)
        const accepted = arr.find(inv => inv.employeeEmail === email && inv.status === 'accepted');
        setCurrentCompany((prev) => {
          if (accepted) {
            if (prev?.id === companyId) return prev;
            return { id: companyId, ...data };
          }
          // si plus accepté sur ce doc, et que c'était notre currentCompany, on nettoie
          return prev?.id === companyId ? null : prev;
        });
      },
      (err) => console.error('inv listener error:', err)
    );
  }, []);

  // Découvre les entreprises où l'utilisateur a potentiellement des invits -> borné + cache TTL
  const discoverInvCompaniesIfNeeded = useCallback(async (email) => {
    if (!email) return { items: [], companies: [] };
    if (!navigator.onLine) {
      // offline → sert cache si dispo
      const cachedOffline = fromCache(INV_CACHE_KEY(email), INV_SCAN_TTL_MS) || { items: [], companies: [] };
      setInvitations(cachedOffline.items || []);
      (cachedOffline.companies || []).forEach((id) => attachInvListenerForCompany(id, email));
      return cachedOffline;
    }

    // Index frais ? On sert et on attache les listeners sans scanner
    const uid = authInstance.currentUser?.uid;
    if (uid) {
      const idx = loadIndexCache(uid);
      const idxTs = idx?.updatedAt || 0;
      if (idx && nowMs() - idxTs <= SETTINGS_INDEX_TTL_MS) {
        const companies = Array.isArray(idx.companies) ? idx.companies : [];
        const pendings = Array.isArray(idx.pendingInvitations) ? idx.pendingInvitations : [];
        setInvitations(pendings);
        companies.forEach((id) => attachInvListenerForCompany(id, email));
        lastIndexTsRef.current = idxTs;
        return { items: pendings, companies };
      }
    }

    // Evite les scans parallèles
    if (discoverInvCompaniesIfNeeded.lock) {
      const cached = fromCache(INV_CACHE_KEY(email), INV_SCAN_TTL_MS) || { items: [], companies: [] };
      return cached;
    }
    discoverInvCompaniesIfNeeded.lock = true;

    try {
      // Sert cache si frais
      const cached = fromCache(INV_CACHE_KEY(email), INV_SCAN_TTL_MS);
      if (cached?.items && cached?.companies) {
        setInvitations(cached.items);
        cached.companies.forEach((id) => attachInvListenerForCompany(id, email));
        return cached;
      }

      // Scan borné (coûts maîtrisés)
      const entreprisesRef = collection(firestore, 'entreprises');
      const snap = await getDocs(query(entreprisesRef, fsLimit(INV_LIMIT_SCAN)));
      const items = [];
      const companies = new Set();

      snap.forEach((docSnap) => {
        const data = docSnap.data() || {};
        const arr = Array.isArray(data.invitations) ? data.invitations : [];

        const pendings = arr
          .filter(inv => inv.employeeEmail === email && inv.status === 'pending')
          .map(inv => ({ id: docSnap.id, ...inv }));
        pendings.forEach((p) => items.push(p));

        const accepted = arr.find(inv => inv.employeeEmail === email && inv.status === 'accepted');
        if (accepted) companies.add(docSnap.id);
        if (pendings.length) companies.add(docSnap.id);
      });

      const out = { items, companies: Array.from(companies) };
      setInvitations(out.items);
      putCache(INV_CACHE_KEY(email), out);
      putCache(INV_KNOWN_COMPANIES_KEY(email), { companies: out.companies });
      persistSettingsIndex(uid, {
        companies: out.companies,
        pendingInvitations: out.items,
        acceptedCompanyId: currentCompany?.id && out.companies.includes(currentCompany.id)
          ? currentCompany.id
          : null,
      }, /*merge*/ true, indexSigRef, lastIndexTsRef);

      // Attache les listeners seulement pour ces entreprises
      out.companies.forEach((id) => attachInvListenerForCompany(id, email));

      return out;
    } finally {
      discoverInvCompaniesIfNeeded.lock = false;
    }
  }, [attachInvListenerForCompany, currentCompany?.id]);

  /* ================================
     Auth + bootstrap (une seule fois)
  ================================= */
  useEffect(() => {
    mountedRef.current = true;

    const unsubAuth = onAuthStateChanged(authInstance, async (firebaseUser) => {
      if (!mountedRef.current) return;
      if (!firebaseUser) {
        navigate('/');
        return;
      }

      try {
        // 1) Sert le cache settings d'abord (UX instantané)
        const cached = fromCache(SETTINGS_CACHE_KEY, null);
        if (cached) {
          setUser((prev) => prev || { email: cached.email, pseudo: cached.pseudo });
          if (cached.company) setCurrentCompany(cached.company);
          const photoCache = fromCache(PROFILE_PHOTO_KEY(firebaseUser.uid), PROFILE_PHOTO_TTL_MS);
          if (photoCache) {
            const url = typeof photoCache === 'string' ? photoCache : photoCache.url;
            if (url) setProfilePhotoURL(url);
          }
        }

        // 1bis) Index local uniquement pour invitations + photo (évite scan initial)
        const idxCache = loadIndexCache(firebaseUser.uid);
        if (idxCache) {
          const sig = indexSignature(idxCache);
          indexSigRef.current = sig;
          lastIndexTsRef.current = idxCache.updatedAt || nowMs();
          saveIndexCache(firebaseUser.uid, idxCache);
          if (idxCache.profilePhotoUrl) {
            setProfilePhotoURL((prev) => prev || idxCache.profilePhotoUrl);
          }
          if (Array.isArray(idxCache.pendingInvitations)) {
            setInvitations(idxCache.pendingInvitations);
          }
          const email = firebaseUser.email || cached?.email || '';
          (idxCache.companies || []).forEach((id) => attachInvListenerForCompany(id, email));
        }

        // 2) Lecture user (1 read, pas de onSnapshot pour Settings)
        const uref = doc(firestore, 'users', firebaseUser.uid);
        const us = await getDoc(uref);
        if (!mountedRef.current) return;

        const data = us.exists() ? us.data() : {};
        const pseudo = data?.pseudo ?? '';
        const email = data?.email ?? firebaseUser.email ?? '';
        const next = { email, pseudo };
        setUser(next);
        putCache(SETTINGS_CACHE_KEY, { ...next, company: currentCompany || null });

        // Si pas de pseudo et non anonyme -> proposer la modale
        if (!firebaseUser.isAnonymous && !pseudo) setShowModal(true);

        // 3) Invitations (hybride - scan borné + listeners ciblés)
        await discoverInvCompaniesIfNeeded(email);

        // 4) Photo de profil (cache + refresh background)
        await fetchProfilePhoto();
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Bootstrap error:', e);
      }
    });

    return () => {
      mountedRef.current = false;
      stopAllInvListeners();
      unsubAuth();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, fetchProfilePhoto, discoverInvCompaniesIfNeeded, stopAllInvListeners]);

  // Index temps réel supprimé (plus d'index Firestore)

  /* ================================
     Réauth pour update email
  ================================= */
  const reauthenticate = async (currentUser) => {
    const pwd = window.prompt('Veuillez entrer votre mot de passe actuel :');
    if (!pwd) throw new Error('Mot de passe requis.');
    const cred = EmailAuthProvider.credential(currentUser.email, pwd);
    await reauthenticateWithCredential(currentUser, cred);
  };

  /* ================================
     Email: Auth + Firestore + propagation invitations
     (optimisé: utilise d’abord les entreprises connues)
  ================================= */
  const handleSaveEmail = async () => {
    const currentUser = authInstance.currentUser;
    const mail = sanitize(editedEmail.trim());
    if (!currentUser || !mail) {
      alert("Veuillez saisir un email valide.");
      return;
    }
    const oldEmail = currentUser.email;

    // 1) Mise à jour Auth (avec réauth si besoin)
    try {
      await firebaseUpdateEmail(currentUser, mail);
    } catch (err) {
      if (err.code === 'auth/requires-recent-login') {
        try {
          await reauthenticate(currentUser);
          await firebaseUpdateEmail(currentUser, mail);
        } catch (reauthErr) {
          console.error('Ré-authentification échouée :', reauthErr);
          alert("Ré-authentification échouée : " + reauthErr.message);
          return;
        }
      } else {
        console.error('Erreur updateEmail:', err);
        alert(err.message);
        return;
      }
    }

    // 2) Firestore + cache
    try {
      const userRef = doc(firestore, 'users', currentUser.uid);
      await updateDoc(userRef, { email: mail });
      setUser((prev) => ({ ...prev, email: mail }));
      putCache(SETTINGS_CACHE_KEY, { email: mail, pseudo: user?.pseudo, company: currentCompany || null });
    } catch (err) {
      console.error('Erreur Firestore update:', err);
      alert("Email mis à jour dans Auth, mais échec Firestore : " + err.message);
      return;
    }

    // 3) Propagation invitations (opération rare → bornée & ciblée)
    try {
      // Entreprises connues (issues des scans précédents)
      const known = fromCache(INV_KNOWN_COMPANIES_KEY(oldEmail), INV_SCAN_TTL_MS)
                 || fromCache(INV_KNOWN_COMPANIES_KEY(user?.email || ''), INV_SCAN_TTL_MS)
                 || { companies: [] };

      let companyIds = Array.isArray(known.companies) ? known.companies.slice() : [];

      // Fallback si aucune connue: scan borné
      if (companyIds.length === 0) {
        const entreprisesRef = collection(firestore, 'entreprises');
        const snapshot = await getDocs(query(entreprisesRef, fsLimit(Math.min(EMAIL_PROPAGATION_LIMIT, INV_LIMIT_SCAN))));
        companyIds = snapshot.docs.map(d => d.id);
      }

      // Parcourt seulement ces entreprises (et abandonne si on dépasse la borne)
      let processed = 0;
      for (const compId of companyIds) {
        if (processed >= EMAIL_PROPAGATION_LIMIT) break;
        processed += 1;

        const entRef = doc(firestore, 'entreprises', compId);
        const entSnap = await getDoc(entRef);
        if (!entSnap.exists()) continue;

        const entData = entSnap.data();
        if (!Array.isArray(entData.invitations)) continue;

        let changed = false;
        const updatedInvitations = entData.invitations.map((inv) => {
          if (inv.employeeEmail === oldEmail) {
            changed = true;
            return { ...inv, employeeEmail: mail };
          }
          return inv;
        });

        if (changed) {
          await updateDoc(entRef, { invitations: updatedInvitations });
        }
      }

      setIsEditingEmail(false);
      setEditedEmail('');
      alert("Email mis à jour avec succès !");
    } catch (err) {
      console.error('Propagation invitations (email) :', err);
      alert("Email mis à jour, mais la propagation des invitations peut être partielle. Réessayer plus tard si nécessaire.");
    }
  };

  /* ================================
     Pseudo
  ================================= */
  const handleSavePseudo = async () => {
    const userId = authInstance.currentUser?.uid;
    const pseudo = sanitize(newPseudo.trim());
    if (userId && pseudo) {
      try {
        const userRef = doc(firestore, 'users', userId);
        await updateDoc(userRef, { pseudo });
        setUser((prev) => ({ ...prev, pseudo }));
        setShowModal(false);
        putCache(SETTINGS_CACHE_KEY, { email: user?.email, pseudo, company: currentCompany || null });
      } catch (e) {
        console.error('Erreur MAJ pseudo:', e);
        alert("Impossible d’enregistrer le pseudo pour le moment.");
      }
    } else {
      alert("Veuillez entrer un pseudo valide.");
    }
  };

  const handlePseudoChange = async () => {
    const userId = authInstance.currentUser?.uid;
    const pseudo = sanitize(newPseudo.trim());
    if (userId && pseudo) {
      try {
        const userRef = doc(firestore, 'users', userId);
        await updateDoc(userRef, { pseudo });
        setUser((prev) => ({ ...prev, pseudo }));
        setIsEditingPseudo(false);
        putCache(SETTINGS_CACHE_KEY, { email: user?.email, pseudo, company: currentCompany || null });
      } catch (e) {
        console.error('Erreur MAJ pseudo:', e);
        alert("Impossible d’enregistrer le pseudo pour le moment.");
      }
    } else {
      alert("Veuillez entrer un pseudo valide.");
    }
  };

  /* ================================
     Password live validation
  ================================= */
  const handlePwdChange = (e) => {
    const v = e.target.value;
    setNewPassword(v);
    setIsPwdValid(PASSWORD_REGEX.test(v));
  };

  /* ================================
     Création compte depuis anonyme
  ================================= */
  const handleCreateAccount = async () => {
    const currentUser = authInstance.currentUser;
    if (!currentUser?.isAnonymous) return;

    const mail   = sanitize(newEmail.trim());
    const pwd    = sanitize(newPassword.trim());
    const pseudo = sanitize(newPseudo.trim());

    if (!mail || !pwd || !pseudo) {
      alert("Veuillez remplir tous les champs.");
      return;
    }
    if (!PASSWORD_REGEX.test(pwd)) {
      alert(PASSWORD_POLICY.message);
      return;
    }

    const credential = EmailAuthProvider.credential(mail, pwd);
    try {
      const linkedUser = await linkWithCredential(currentUser, credential);
      const userRef = doc(firestore, 'users', linkedUser.user.uid);
      await setDoc(
        userRef,
        { email: mail, pseudo, createdAt: serverTimestamp(), role: 'client' },
        { merge: true }
      );
      // Poser needsEmailVerification: true + déclencher la gate de vérification email
      try {
        const idToken = await linkedUser.user.getIdToken();
        const apiBase = ((process.env.REACT_APP_API_URL || '').replace(/\/+$/, '')) ||
          (process.env.NODE_ENV === 'production' ? window.location.origin : 'http://localhost:5099');
        await fetch(`${apiBase}/api/auth/flag-verification`, {
          method: 'POST',
          credentials: 'include',
          headers: { Authorization: `Bearer ${idToken}` },
        });
        await reloadEmailVerified(true);
      } catch (flagErr) {
        console.warn('flag-verification non critique :', flagErr);
      }

      setUser({ email: mail, pseudo });
      setShowCreateAccountModal(false);
      putCache(SETTINGS_CACHE_KEY, { email: mail, pseudo, company: currentCompany || null });
    } catch (error) {
      console.error("Erreur lors de la création du compte", error);
      if (error.code === 'auth/email-already-in-use') {
        alert("Email déjà utilisé, veuillez sélectionner un autre email.");
      } else {
        alert("Erreur lors de la création du compte : " + error.message);
      }
    }
  };

  /* ================================
     Invitations: Accept / Reject / Désaffiliation
  ================================= */
  const handleAcceptInvitation = async (invitationCompanyId) => {
    try {
      const currentUser = authInstance.currentUser;
      if (!currentUser) {
        alert("Erreur : vous devez être connecté pour accepter l'invitation.");
        return;
      }

      // Si déjà affilié ailleurs, propose de remplacer
      if (currentCompany && currentCompany.id !== invitationCompanyId) {
        const remplacement = window.confirm(
          "Vous êtes déjà affilié à une entreprise, souhaitez-vous remplacer cette affiliation par l'entreprise qui vous invite ?"
        );
        if (!remplacement) return;

        // Marque 'rejected' sur l'ancienne
        const ancienneEntrepriseRef = doc(firestore, 'entreprises', currentCompany.id);
        const ancienneSnapshot = await getDoc(ancienneEntrepriseRef);
        if (ancienneSnapshot.exists()) {
          const ancienneData = ancienneSnapshot.data();
          if (Array.isArray(ancienneData.invitations)) {
            const majAnciennesInvitations = ancienneData.invitations.map(inv => {
              if (inv.employeeEmail === currentUser.email && inv.status === 'accepted') {
                return { ...inv, status: 'rejected' };
              }
              return inv;
            });
            await updateDoc(ancienneEntrepriseRef, { invitations: majAnciennesInvitations });
          }
        }
        setCurrentCompany(null);
      }

      // Accepte sur la nouvelle
      const entrepriseRef = doc(firestore, 'entreprises', invitationCompanyId);
      const entrepriseSnapshot = await getDoc(entrepriseRef);
      if (entrepriseSnapshot.exists()) {
        const data = entrepriseSnapshot.data();
        if (Array.isArray(data.invitations)) {
          const updatedInvitations = data.invitations.map((inv) =>
            inv.employeeEmail === currentUser.email && inv.status === 'pending'
              ? { ...inv, status: 'accepted', employeeUid: currentUser.uid }
              : inv
          );
          await updateDoc(entrepriseRef, { invitations: updatedInvitations });

          // Mémorise le lien employé→marchand sur le doc user pour les règles Storage
          if (data.userId) {
            try {
              await updateDoc(doc(firestore, 'users', currentUser.uid), { acceptedCompanyUID: data.userId });
            } catch (e) {
              console.error('[invite] acceptedCompanyUID write failed:', e);
            }
          }

          alert('Invitation acceptée');

          // Mise à jour locale immédiate, le listener confirmera
          setCurrentCompany({ id: invitationCompanyId, ...data, invitations: updatedInvitations });
          setInvitations((prev) => prev.filter(x => x.id !== invitationCompanyId));

          // Mémorise côté cache la nouvelle entreprise connue
          const known = fromCache(INV_KNOWN_COMPANIES_KEY(user?.email || ''), INV_SCAN_TTL_MS) || { companies: [] };
          const arr = new Set(known.companies || []);
          arr.add(invitationCompanyId);
          putCache(INV_KNOWN_COMPANIES_KEY(user?.email || ''), { companies: Array.from(arr) });
        } else {
          alert("Erreur : aucune invitation trouvée.");
        }
      } else {
        alert("Erreur : document entreprise introuvable.");
      }
    } catch (error) {
      console.error("Erreur lors de l'acceptation de l'invitation :", error);
      alert("Une erreur est survenue lors de l'acceptation de l'invitation.");
    }
  };

  const handleRejectInvitation = async (invitationCompanyId) => {
    try {
      const entrepriseRef = doc(firestore, 'entreprises', invitationCompanyId);
      const entrepriseSnapshot = await getDoc(entrepriseRef);
      if (entrepriseSnapshot.exists()) {
        const data = entrepriseSnapshot.data();
        if (Array.isArray(data.invitations)) {
          const updatedInvitations = data.invitations.map((invitation) =>
            invitation.employeeEmail === user?.email && invitation.status === 'pending'
              ? { ...invitation, status: 'rejected' }
              : invitation
          );
          await updateDoc(entrepriseRef, { invitations: updatedInvitations });
          alert('Invitation rejetée');

          setInvitations((prev) => prev.filter(x => x.id !== invitationCompanyId));
        } else {
          alert("Erreur : aucune invitation trouvée.");
        }
      } else {
        alert("Erreur : document entreprise introuvable.");
      }
    } catch (error) {
      console.error("Erreur lors du rejet de l'invitation :", error);
      alert("Une erreur est survenue lors du rejet de l'invitation.");
    }
  };

  const handleDesaffiliate = async (email) => {
    if (typeof email !== 'string') {
      console.error("Email invalide :", email);
      return;
    }
    try {
      const entrepriseRef = collection(firestore, 'entreprises');
      // Borne: on consulte d’abord les entreprises connues
      const known = fromCache(INV_KNOWN_COMPANIES_KEY(email), INV_SCAN_TTL_MS) || { companies: [] };
      let docsToVisit = known.companies || [];

      if (!docsToVisit.length) {
        // fallback borné
        const querySnapshot = await getDocs(query(entrepriseRef, fsLimit(INV_LIMIT_SCAN)));
        docsToVisit = querySnapshot.docs.map(d => d.id);
      }

      let emailFound = false;

      for (const entrepriseId of docsToVisit) {
        const entrepriseDocRef = doc(firestore, 'entreprises', entrepriseId);
        const entrepriseSnapshot = await getDoc(entrepriseDocRef);
        if (!entrepriseSnapshot.exists()) continue;

        const entrepriseData = entrepriseSnapshot.data();
        if (!Array.isArray(entrepriseData.invitations)) continue;

        const idx = entrepriseData.invitations.findIndex(
          (inv) => inv.employeeEmail === email && inv.status === 'accepted'
        );
        if (idx !== -1) {
          emailFound = true;
          const updatedInvitations = [...entrepriseData.invitations];
          updatedInvitations[idx] = { ...updatedInvitations[idx], status: 'rejected' };
          await updateDoc(entrepriseDocRef, { invitations: updatedInvitations });

          // Efface le lien employé→marchand du doc user
          const authCurrentUser = authInstance?.currentUser;
          if (authCurrentUser) {
            try {
              await updateDoc(doc(firestore, 'users', authCurrentUser.uid), { acceptedCompanyUID: null });
            } catch (e) {
              console.error('[desaffiliate] acceptedCompanyUID clear failed:', e);
            }
          }

          alert("Désaffiliation effectuée avec succès");
          setCurrentCompany(null);
          break; // une seule affiliation à la fois
        }
      }

      if (!emailFound) {
        alert("Votre compte ne semble pas affilié à une société");
      }
    } catch (error) {
      console.error('Erreur désaffiliation :', error);
      alert("Votre compte ne semble pas affilié à une société");
    }
  };

  /* ================================
     Logout
  ================================= */
  const handleLogout = async () => {
    try {
      await logoutSafely(authInstance);
    } catch (error) {
      console.error("Erreur lors de la déconnexion :", error?.message || error);
    } finally {
      try {
        localStorage.removeItem('userEmail');
        localStorage.removeItem('userRole');
      } catch {}
      window.location.href = "/";
    }
  };

  /* ================================
     UI — VISUELS UNIQUEMENT (design system --eb-*)
  ================================= */
  const pseudoTextStyle = useMemo(() => ({
    textAlign: 'center',
    fontSize: 20,
    fontWeight: 700,
    letterSpacing: '-0.015em',
    color: 'var(--eb-text)',
  }), []);

  const currentFirebaseUser = authInstance.currentUser;
  const isAnonymous = currentFirebaseUser?.isAnonymous;

  return (
    <div className="dashboard-container client-settings-page">
      <ClientNavbar />
      <div className="cs-wrap">
        <header className="eb-page-header">
          <div>
            <h1 className="eb-page-title">Paramètres</h1>
          </div>
        </header>

        {/* Profil */}
        <section className="cs-card cs-profile">
          <button
            type="button"
            className="profile-photo-container cs-avatar"
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
            aria-label="Modifier la photo de profil"
          >
            {profilePhotoURL ? (
              <img
                src={profilePhotoURL}
                alt="Profil"
                className="profile-photo"
              />
            ) : (
              <FaUser aria-hidden="true" />
            )}
            <span className="cs-avatar__edit" aria-hidden="true"><FiEdit2 size={13} /></span>
          </button>
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={handleProfilePhotoChange}
            accept="image/*"
          />
          <div className="cs-profile__name">
            {isAnonymous ? (
              <span style={pseudoTextStyle}>
                {user?.pseudo || user?.email || t('Mon compte')}
              </span>
            ) : (
              <AccountSwitcher
                label={user?.pseudo || user?.email || t('Mon compte')}
                labelStyle={pseudoTextStyle}
              />
            )}
          </div>
        </section>

        <div className="eb-grid-2 cs-grid">
          {/* ====== CARD PRINCIPALE SETTINGS ====== */}
          <section className="cs-card cs-card--sectioned">
            <div className="eb-card__head">
              <h2 className="eb-card__title cs-section-title">{t('Info')}</h2>
            </div>
            <div className="eb-card__body">
              {isAnonymous ? (
                <div className="anonymous-container cs-anon">
                  <button
                    type="button"
                    className="create-account-button eb-btn eb-btn--primary eb-btn--lg"
                    onClick={() => setShowCreateAccountModal(true)}
                  >
                    {t('Créer mon compte')}
                  </button>

                  {showCreateAccountModal && ReactDOM.createPortal(
                    <div className="cs-overlay">
                      <div className="cs-modal eb-fade-up" role="dialog" aria-modal="true">
                        <button
                          type="button"
                          onClick={() => setShowCreateAccountModal(false)}
                          className="cs-modal__close"
                          aria-label="Fermer"
                        >
                          &times;
                        </button>

                        <h2 className="cs-modal__title">{t('Créer mon compte')}</h2>

                        <input
                          type="email"
                          className="eb-input"
                          value={newEmail}
                          onChange={(e) => setNewEmail(e.target.value)}
                          placeholder={t('Email')}
                          aria-label={t('Email')}
                          autoComplete="email"
                          inputMode="email"
                        />

                        {/* Mot de passe avec validation live */}
                        <div className="cs-pwd">
                          <PasswordField
                            value={newPassword}
                            onChange={handlePwdChange}
                            placeholder={t('Mot de passe')}
                            className="eb-input"
                            inputStyle={newPassword && !isPwdValid ? { borderColor: 'var(--eb-danger)' } : undefined}
                            autoComplete="new-password"
                            pattern={PASSWORD_REGEX.source}
                            title={PASSWORD_POLICY.message}
                            aria-label={t('Mot de passe')}
                            aria-invalid={!!newPassword && !isPwdValid}
                          />
                        </div>

                        <input
                          type="text"
                          className="eb-input"
                          value={newPseudo}
                          onChange={(e) => setNewPseudo(e.target.value)}
                          placeholder={t('Pseudo')}
                          aria-label={t('Pseudo')}
                        />

                        <button
                          type="button"
                          onClick={handleCreateAccount}
                          className="eb-btn eb-btn--primary eb-btn--lg eb-btn--block"
                          disabled={!newEmail.trim() || !newPassword.trim() || !newPseudo.trim() || !isPwdValid}
                        >
                          {t('Créer')}
                        </button>
                      </div>
                    </div>,
                    document.body
                  )}
                </div>
              ) : (
                <>
                  {/* Modale définir pseudo */}
                  {!isAnonymous && showModal && ReactDOM.createPortal(
                    <div className="cs-overlay">
                      <div className="cs-modal eb-fade-up" role="dialog" aria-modal="true">
                        <h2 className="cs-modal__title" style={{ paddingRight: 0 }}>{t('Definir_pseudo')}</h2>
                        <input
                          type="text"
                          className="eb-input"
                          value={newPseudo}
                          onChange={(e) => setNewPseudo(e.target.value)}
                          placeholder={t('Votre_pseudo')}
                          aria-label={t('Votre_pseudo')}
                        />
                        <button
                          type="button"
                          onClick={handleSavePseudo}
                          className="eb-btn eb-btn--primary eb-btn--lg eb-btn--block"
                        >
                          {t('Enregistrer')}
                        </button>
                      </div>
                    </div>,
                    document.body
                  )}

                  <div className="user-info cs-rows">
                    {/* Email */}
                    <div className="row-email cs-row">
                      <span className="cs-row__label">{t('Email')}</span>
                      {isEditingEmail ? (
                        <div className="cs-row__edit">
                          <input
                            type="email"
                            className="eb-input"
                            value={editedEmail}
                            onChange={e => setEditedEmail(e.target.value)}
                            placeholder="Nouvel email"
                            aria-label="Nouvel email"
                            inputMode="email"
                          />
                          <div className="cs-row__actions">
                            <button
                              type="button"
                              className="eb-btn eb-btn--primary"
                              onClick={handleSaveEmail}
                            >
                              {t('Enregistrer')}
                            </button>
                            <button
                              type="button"
                              className="eb-btn eb-btn--secondary cs-icon-btn"
                              onClick={()=>setIsEditingEmail(false)}
                              aria-label={t('Annuler')}
                              title={t('Annuler')}
                            >
                              ✕
                            </button>
                          </div>
                        </div>
                      ) : (
                        <span className="inline-center-mobile cs-row__value">
                          <span className="cs-row__text">{user?.email}</span>
                          <button
                            type="button"
                            className="eb-btn eb-btn--secondary cs-icon-btn"
                            onClick={() => {
                              setIsEditingEmail(true);
                              setEditedEmail(user?.email || '');
                            }}
                            aria-label={t('Modifier')}
                            title={t('Modifier')}
                          >
                            <FiEdit2 size={16} />
                          </button>
                        </span>
                      )}
                    </div>

                    {/* Pseudo */}
                    <div className="row-pseudo cs-row">
                      <span className="cs-row__label">{t('Pseudo')}</span>
                      {isEditingPseudo ? (
                        <div className="cs-row__edit">
                          <input
                            type="text"
                            className="eb-input"
                            value={newPseudo}
                            onChange={(e) => setNewPseudo(e.target.value)}
                            placeholder={t('Nouveau_pseudo')}
                            aria-label={t('Nouveau_pseudo')}
                          />
                          <div className="cs-row__actions">
                            <button
                              type="button"
                              className="eb-btn eb-btn--primary"
                              onClick={handlePseudoChange}
                            >
                              {t('MAJ')}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <span className="inline-center-mobile cs-row__value">
                          <span className="cs-row__text">{user?.pseudo}</span>
                          <button
                            type="button"
                            className="eb-btn eb-btn--secondary cs-icon-btn"
                            onClick={() => {
                              setIsEditingPseudo(true);
                              setNewPseudo(user?.pseudo || '');
                            }}
                            aria-label={t('Modifier')}
                            title={t('Modifier')}
                          >
                            <FiEdit2 size={16} />
                          </button>
                        </span>
                      )}
                    </div>

                    {/* Entreprise actuelle */}
                    {currentCompany &&
                      currentCompany.invitations?.some(
                        (inv) => inv.employeeEmail === user?.email && inv.status === 'accepted'
                      ) && (
                        <div className="row-company cs-row">
                          <span className="cs-row__label">{t('Entreprise')}</span>
                          <span className="cs-row__value">
                            <span className="company-name cs-row__text">{currentCompany.pseudo || currentCompany.createdBy}</span>
                            <button
                              type="button"
                              className="eb-btn eb-btn--secondary eb-btn--sm cs-danger-btn"
                              onClick={() => handleDesaffiliate(user?.email)}
                            >
                              {t('Désaffiliation')}
                            </button>
                          </span>
                        </div>
                      )}

                    {/* Invitations en attente */}
                    {invitations.length > 0 && (
                      <div className="invitations cs-invitations">
                        <h3 className="cs-section-title cs-section-title--sub">{t('Invitation')}</h3>
                        <ul className="cs-invite-list">
                          {invitations.map((invitation) => (
                            <li key={invitation.id} className="cs-invite">
                              <p className="cs-invite__text">
                                {t('Invitation_entreprise')} {invitation.invitedBy}
                              </p>
                              <div className="cs-row__actions">
                                <button
                                  type="button"
                                  className="eb-btn eb-btn--primary eb-btn--sm"
                                  onClick={() => handleAcceptInvitation(invitation.id)}
                                >
                                  {t('Accepter')}
                                </button>
                                <button
                                  type="button"
                                  className="eb-btn eb-btn--secondary eb-btn--sm"
                                  onClick={() => handleRejectInvitation(invitation.id)}
                                >
                                  {t('Refuser')}
                                </button>
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </section>

          <div className="eb-stack">
            {/* Préférences */}
            <section className="cs-card cs-card--sectioned">
              <div className="eb-card__head">
                <h2 className="eb-card__title cs-section-title">{t('Préférences')}</h2>
              </div>
              <div className="eb-card__body">
                {/* Sélecteur de langue */}
                <div id="language-selector" className="cs-lang">
                  <LanguageSelector />
                  <div className="cs-muted">{t('Changer la langue de l’interface')}</div>
                </div>
              </div>
            </section>

            {/* Documents juridiques */}
            <section className="cs-card cs-card--sectioned">
              <div className="eb-card__head">
                <h2 className="eb-card__title cs-section-title">{t('Documents juridiques')}</h2>
              </div>
              <div className="eb-card__body cs-legal">
                <button
                  type="button"
                  className="cs-doc-link"
                  onClick={() => navigate('/legal/cgu')}
                >
                  {t('CONDITIONS GENERALES')} <FiChevronRight aria-hidden="true" />
                </button>
              </div>
            </section>
          </div>
        </div>

        {/* Déconnexion */}
        <section className="cs-card cs-session">
          <div className="eb-row">
            <div className="eb-row__main">
              <p className="cs-session__title">{t('Session')}</p>
              <p className="cs-muted cs-session__hint">{t('Se déconnecter de ce compte sur cet appareil.')}</p>
            </div>
            <div className="eb-row__actions logout-container">
              <button
                type="button"
                className="logout-button eb-btn eb-btn--secondary cs-danger-btn"
                onClick={handleLogout}
              >
                {t('Déconnexion')}
              </button>
            </div>
          </div>
        </section>
      </div>

      <style dangerouslySetInnerHTML={{
        __html: `
.dashboard-container.client-settings-page { align-items: stretch; }
.cs-wrap { width: 100%; display: flex; flex-direction: column; gap: 20px; }
.cs-grid { align-items: stretch; }
.cs-wrap .eb-page-header { margin-bottom: 8px; }
.cs-wrap .eb-page-title { text-align: left; }

.cs-card {
  background: var(--eb-surface);
  border: 1px solid var(--eb-border);
  border-radius: var(--eb-radius-lg);
  box-shadow: var(--eb-shadow-sm);
  padding: 24px;
}
.cs-section-title {
  margin: 0 0 8px;
  text-align: left;
  font-size: 17px;
  font-weight: 700;
  color: var(--eb-text);
}
.cs-card.cs-card--sectioned { padding: 0; }
.cs-card--sectioned > .eb-card__body { padding: 8px 20px 16px; }
.cs-card--sectioned .eb-card__head .cs-section-title { margin: 0; font-size: 15px; font-weight: 650; }
.cs-grid > .cs-card--sectioned { height: 100%; }
.cs-section-title--sub { margin-top: 8px; font-size: 15px; }

/* Profil */
.cs-profile { display: flex; align-items: center; gap: 20px; }
.cs-avatar {
  position: relative;
  flex-shrink: 0;
  width: 88px;
  height: 88px;
  padding: 0;
  border-radius: 50%;
  border: 3px solid var(--eb-surface);
  background: var(--eb-brand-50);
  box-shadow: var(--eb-shadow-md);
  color: var(--eb-brand-300);
  font-size: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}
.cs-avatar img { width: 100%; height: 100%; border-radius: 50%; object-fit: cover; display: block; }
.cs-avatar:focus-visible { outline: none; box-shadow: var(--eb-ring); }
.cs-avatar__edit {
  position: absolute;
  right: -2px;
  bottom: -2px;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: var(--eb-brand);
  color: #fff;
  border: 2px solid var(--eb-surface);
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.cs-profile__name { min-width: 0; display: flex; }

/* Lignes d'info */
.cs-rows { display: flex; flex-direction: column; }
.cs-row {
  display: grid;
  grid-template-columns: 140px minmax(0, 1fr);
  align-items: center;
  gap: 12px;
  padding: 14px 0;
  border-bottom: 1px solid var(--eb-border);
}
.cs-rows > .cs-row:last-of-type { border-bottom: 0; }
.cs-row__label { font-size: 14px; font-weight: 600; color: var(--eb-text-2); }
.cs-row__value { display: flex; align-items: center; justify-content: space-between; gap: 12px; min-width: 0; }
.cs-row__text { min-width: 0; overflow-wrap: anywhere; color: var(--eb-text); font-size: 15px; }
.cs-row__edit { display: flex; flex-direction: column; gap: 10px; min-width: 0; }
.cs-row__actions { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
.cs-icon-btn { width: 40px; min-width: 40px; padding: 0; flex-shrink: 0; color: var(--eb-brand); }
.cs-danger-btn { color: var(--eb-danger); }
.cs-danger-btn:hover:not(:disabled) { background: var(--eb-danger-bg); color: var(--eb-danger-hover); border-color: #fbd0cb; }

.cs-invitations { padding-top: 12px; border-top: 1px solid var(--eb-border); }
.cs-invite-list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 10px; }
.cs-invite {
  padding: 14px;
  border-radius: var(--eb-radius);
  border: 1px solid var(--eb-border);
  background: var(--eb-surface-2);
}
.cs-invite__text { margin: 0 0 10px; font-size: 14.5px; color: var(--eb-text-2); }

.cs-anon { display: flex; justify-content: flex-start; padding-top: 8px; }

/* Langue + documents + session */
.cs-lang { display: flex; flex-direction: column; align-items: flex-start; gap: 8px; padding-top: 6px; }
.cs-muted { color: var(--eb-muted); font-size: 13px; }
.cs-legal { display: flex; flex-direction: column; }
.cs-card--sectioned > .eb-card__body.cs-legal { padding-top: 0; padding-bottom: 0; }
.cs-doc-link {
  display: flex; align-items: center; justify-content: space-between; gap: 12px; width: 100%; min-height: 52px; padding: 0;
  background: none; border: 0; border-bottom: 1px solid var(--eb-border); color: var(--eb-text);
  font-size: 14.5px; font-weight: 500; text-align: left; cursor: pointer;
}
.cs-doc-link:last-child { border-bottom: 0; }
.cs-doc-link:hover { color: var(--eb-brand); }
.cs-doc-link svg { color: var(--eb-subtle); flex-shrink: 0; }
.cs-card.cs-session { padding: 4px 20px; }
.cs-session__title { margin: 0; font-size: 14.5px; font-weight: 600; color: var(--eb-text); }
.cs-session__hint { margin: 2px 0 0; }
.cs-session .logout-container { margin: 0; width: auto; display: flex; }
#language-selector > :first-child { margin-top: 0; }
#language-selector h3 { margin: 0 0 10px; text-align: left; font-size: 17px; font-weight: 700; color: var(--eb-text); }
#language-selector h3:empty { display: none; }

#language-selector select {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  min-height: 42px;
  padding: 0 38px 0 14px;
  border-radius: var(--eb-radius-sm);
  border: 1px solid var(--eb-border-strong);
  background-color: var(--eb-surface);
  color: var(--eb-text);
  font-size: 14.5px;
  background-image: linear-gradient(45deg, transparent 50%, var(--eb-muted) 50%),
                    linear-gradient(135deg, var(--eb-muted) 50%, transparent 50%);
  background-position: calc(100% - 18px) 50%, calc(100% - 13px) 50%;
  background-size: 5px 5px, 5px 5px;
  background-repeat: no-repeat;
}
#language-selector button {
  min-height: 40px;
  padding: 0 14px;
  border-radius: var(--eb-radius-sm);
  border: 1px solid var(--eb-border-strong);
  background: var(--eb-surface);
  color: var(--eb-text-2);
  margin: 0 4px 0 0;
}
#language-selector button:hover { background: var(--eb-surface-2); }
#language-selector .active, #language-selector .selected {
  color: var(--eb-brand);
  border-color: var(--eb-brand-300);
  background: var(--eb-brand-50);
}

/* Modales */
.cs-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(15, 35, 56, 0.55);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
}
.cs-modal {
  position: relative;
  width: 100%;
  max-width: 440px;
  max-height: calc(100vh - 32px);
  max-height: calc(100dvh - 32px);
  overflow-y: auto;
  background: var(--eb-surface);
  border-radius: var(--eb-radius-xl);
  box-shadow: var(--eb-shadow-lg);
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  text-align: left;
}
.cs-modal__title { margin: 0 0 4px; padding-right: 44px; font-size: 22px; font-weight: 700; letter-spacing: -0.02em; text-align: left; }
.cs-modal__close {
  position: absolute;
  top: 14px;
  right: 14px;
  width: 40px;
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: var(--eb-radius-sm);
  background: transparent;
  color: var(--eb-muted);
  font-size: 26px;
  line-height: 1;
  cursor: pointer;
}
.cs-modal__close:hover { background: var(--eb-surface-2); color: var(--eb-text); }
.cs-pwd > div { margin-bottom: 0 !important; }
.cs-pwd > ul { margin: 10px 0 0 !important; }
.cs-pwd .eb-input { padding-right: 44px; }

@media (max-width: 600px) {
  .cs-card { padding: 20px 16px; }
  .cs-profile { flex-direction: column; text-align: center; gap: 14px; }
  .cs-profile__name { justify-content: center; }
  .cs-row { grid-template-columns: 1fr; gap: 6px; padding: 12px 0; }
  .cs-row__label { font-size: 13px; color: var(--eb-muted); }
  .inline-center-mobile { justify-content: space-between !important; }
  .cs-card.cs-card--sectioned { padding: 0; }
  .cs-card--sectioned > .eb-card__body { padding: 6px 16px 14px; }
  .cs-card.cs-session { padding: 4px 16px; }
  .cs-session .logout-container, .cs-session .logout-button { width: 100%; }
  .cs-anon .eb-btn { width: 100%; }
  .cs-overlay { align-items: flex-end; padding: 0; }
  .cs-modal {
    max-width: none;
    max-height: calc(100dvh - 24px);
    border-radius: var(--eb-radius-xl) var(--eb-radius-xl) 0 0;
    padding: 22px 20px calc(20px + env(safe-area-inset-bottom, 0px));
  }
}
        `
      }} />
    </div>
  );
};

export default ClientSettings;
